import { theme } from '../../styles/theme';
import { Zap, FlaskConical, LogOut } from 'lucide-react';
import logo from '../../assets/automat-it-logo-colored.svg';
import { useIsMobile } from '../../hooks/useIsMobile';

interface HeaderProps {
  user?: {
    username: string;
    signInDetails?: {
      loginId?: string;
    };
  } | null;
  onSignOut?: () => void;
  developmentMode?: boolean;
  errorSimEnabled?: boolean;
  onToggleErrorSim?: () => void;
  mockEnabled?: boolean;
  onToggleMock?: () => void;
}

const devButtonStyle = (active: boolean, activeColor: string, activeBg: string, activeBorder: string) => ({
  display: 'flex' as const, alignItems: 'center' as const, gap: '0.5rem',
  padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
  borderRadius: theme.borderRadius.full,
  fontSize: '0.65rem', fontWeight: 700,
  textTransform: 'uppercase' as const, letterSpacing: '0.15em',
  cursor: 'pointer' as const, transition: 'all 0.2s ease',
  background: active ? activeBg : theme.colors.borderLight,
  color: active ? activeColor : theme.colors.textMuted,
  border: `1px solid ${active ? activeBorder : theme.colors.border}`,
});

export function Header({ user, onSignOut, developmentMode, errorSimEnabled, onToggleErrorSim, mockEnabled, onToggleMock }: HeaderProps) {
  const mobile = useIsMobile();

  return (
    <header style={{
      height: mobile ? 56 : 80,
      padding: mobile ? `0 ${theme.spacing.lg}` : `0 ${theme.spacing['3xl']}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      <a href="https://www.automat-it.com/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
        <img src={logo} alt="Automat-it" style={{ height: mobile ? '24px' : '30%', cursor: 'pointer' }} />
      </a>
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        {!mobile && developmentMode && onToggleMock && (
          <button
            onClick={onToggleMock}
            style={devButtonStyle(!!mockEnabled, theme.colors.infoText, 'rgba(59,130,246,0.1)', theme.colors.info)}
          >
            <FlaskConical size={14} />
            {mockEnabled ? 'Mock On' : 'Mock Off'}
          </button>
        )}
        {!mobile && developmentMode && onToggleErrorSim && (
          <button
            onClick={onToggleErrorSim}
            style={devButtonStyle(!!errorSimEnabled, theme.colors.errorText, 'rgba(239,68,68,0.1)', theme.colors.error)}
          >
            <Zap size={14} />
            {errorSimEnabled ? 'Error On' : 'Error Off'}
          </button>
        )}
        {user && (
          <button
            onClick={onSignOut}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              padding: theme.spacing.md,
              background: 'transparent',
              color: theme.colors.navy,
              border: 'none',
              borderRadius: theme.borderRadius.full,
              fontSize: theme.fontSize.md,
              fontWeight: theme.fontWeight.bold,
              cursor: 'pointer',
              transition: `background ${theme.transitions.fast}, transform ${theme.transitions.fast}, box-shadow ${theme.transitions.fast}`,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = theme.colors.teal;
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = theme.shadows.button;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = theme.colors.navy;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <LogOut size={20} />
          </button>
        )}
      </div>
    </header>
  );
}
